/**
 * @file License.tsx
 * @path apps/website/src/pages/legal/License.tsx
 * @description
 */

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SeoHelmet as SEOHelmet } from "@/components";
import { NAV_LINKS, siteMetadata } from "@/config";
import { formatPhone } from "@sirbastians/utils";

const License = () => {
  return (
    <>
      <SEOHelmet
        pageType="default"
        title="License Information | Sir Bastian's Shuttle Service"
        description="Sir Bastian's Shuttle Service licensing and regulatory compliance information."
        url="/license"
      />

      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Header */}
          <header className="mb-12">
            <h1 className="text-4xl font-bold mb-4">License Information</h1>
            <p className="text-muted-foreground">
              Our operating licenses and regulatory compliance
            </p>
          </header>

          {/* Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">
                1. Operating Licenses
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Sir Bastian's Shuttle Service operates as a fully licensed and
                registered passenger transport service in Cape Town, South
                Africa. We comply with all relevant transportation regulations
                and safety standards.
              </p>
              <div className="bg-muted/50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">
                  Regulatory Compliance
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    Registered with the Provincial Regulatory Entity (PRE)
                  </li>
                  <li>Operating permits for passenger shuttle services</li>
                  <li>Tour operator registration</li>
                  <li>Compliance with National Land Transport Act</li>
                  <li>Valid roadworthy certificates for all vehicles</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                2. Driver Licensing
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All our drivers hold valid Professional Driving Permits (PrDP)
                as required by South African law for commercial passenger
                transport.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  Valid Code 10 or Code 14 driver's licenses (depending on
                  vehicle type)
                </li>
                <li>
                  Professional Driving Permit (PrDP) for passenger transport
                </li>
                <li>Regular medical fitness assessments</li>
                <li>Background checks and clearances</li>
                <li>Tourism guide certification for tour drivers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                3. Insurance Coverage
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We maintain comprehensive insurance coverage for all operations:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Public liability insurance</li>
                <li>Passenger liability insurance</li>
                <li>Comprehensive vehicle insurance</li>
                <li>Third-party insurance</li>
                <li>Goods in transit insurance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                4. Safety Standards
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We adhere to strict safety protocols:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Regular vehicle maintenance and safety inspections</li>
                <li>GPS tracking and monitoring systems</li>
                <li>Driver training and ongoing assessment</li>
                <li>Emergency response procedures</li>
                <li>COVID-19 safety protocols (where applicable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                5. Business Registration
              </h2>
              <div className="bg-muted/50 p-6 rounded-lg">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>Sir Bastian's Shuttle Service</strong>
                  <br />
                  Registered business operating in Cape Town, South Africa
                  <br />
                  VAT Registered
                  <br />
                  BEE Compliant
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  For verification of our licenses and registrations, please
                  contact us directly.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                6. Website License
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This website and its content are proprietary to Sir Bastian's
                Shuttle Service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  © 2025 Sir Bastian's Shuttle Service. All rights reserved.
                </li>
                <li>Website design and content are protected by copyright</li>
                <li>
                  Unauthorized use, reproduction, or distribution is prohibited
                </li>
                <li>
                  Images and logos are trademarks of Sir Bastian's Shuttle
                  Service
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                7. Third-Party Licenses
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This website uses open-source software and third-party services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>React and associated libraries (MIT License)</li>
                <li>Tailwind CSS (MIT License)</li>
                <li>Lucide Icons (ISC License)</li>
                <li>Google Fonts (Open Font License)</li>
                <li>Formspree for form processing (third-party service)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                We acknowledge and thank the open-source community for their
                contributions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For inquiries about our licenses, compliance, or website usage:
              </p>
              <div className="bg-muted/50 p-6 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>
                    {siteMetadata.title}
                    <br />
                  </strong>
                  <br />
                  Email: {siteMetadata.email}
                  <br />
                  Phone: {formatPhone(siteMetadata.phone)}
                  <br />
                  Address: Cape Town, South Africa
                </p>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-6 text-sm">
              <Link
                to={NAV_LINKS.experiences.path}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Experiences
              </Link>
              <Link
                to="/privacy-policy"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to={NAV_LINKS.contactUs.path}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default License;
